/**
 * Object Relationship Queries
 *
 * PE-775: Manages schema-level relationships between objects.
 * This defines how objects are connected (Lookup, Master-Detail) at the metadata level.
 *
 * Different from relatedRecordsService.ts which handles runtime record-to-record links.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createPersistedMockData } from '../mockDataManager';
import {
  createGobodoRelationshipType,
  getGobodoRelationshipTypes,
  getGobodoRelationshipTypeByKey,
  getGobodoObjectTypeOutgoingRelationships,
  getGobodoObjectTypeIncomingRelationships,
  deleteGobodoRelationshipType,
  updateGobodoRelationshipType
} from '../gobodo/gobodoRelationshipService';
import { resolveObjectKey } from '../gobodo/gobodoObjectService';
import { objectKeys } from './objectQueries';
import type { CustomObject } from './objectQueries';

import { USE_GOBODO_API } from '../../utils/gobodoUtils';

// ============================================
// TYPES
// ============================================

export type RelationshipType = 'lookup' | 'master-detail';

export interface ObjectRelationship {
  id: string;
  /** The object that owns this relationship field */
  sourceObjectId: string;
  sourceObjectName: string;
  sourceObjectLabel: string;
  /** The field name on the source object (API name) */
  fieldName: string;
  /** Display label for the relationship field */
  fieldLabel: string;
  /** The object being referenced */
  targetObjectId: string;
  targetObjectName: string;
  targetObjectLabel: string;
  /** Type of relationship */
  relationshipType: RelationshipType;
  /** For master-detail: cascade delete child records */
  cascadeDelete: boolean;
  /** For master-detail: share access with parent */
  shareAccessLevel: 'ReadOnly' | 'ReadWrite' | 'None';
  /** Child relationship name (for reverse lookup) */
  childRelationshipName: string;
  /** Description of the relationship */
  description?: string;
  /** Is this relationship required? */
  isRequired: boolean;
  /** Is this a standard/system relationship? */
  isStandard: boolean;
  /** Is this relationship active? */
  isActive: boolean;
  /** Filter criteria for lookup (optional) */
  lookupFilter?: string;
  /** Created metadata */
  createdBy: string;
  createdDate: string;
  lastModified: string;
}

export interface CreateRelationshipInput {
  sourceObjectId: string;
  sourceObjectName: string;
  sourceObjectLabel: string;
  fieldName: string;
  fieldLabel: string;
  targetObjectId: string;
  targetObjectName: string;
  targetObjectLabel: string;
  relationshipType: RelationshipType;
  cascadeDelete?: boolean;
  shareAccessLevel?: 'ReadOnly' | 'ReadWrite' | 'None';
  childRelationshipName: string;
  description?: string;
  isRequired?: boolean;
  lookupFilter?: string;
}

export interface UpdateRelationshipInput {
  id: string;
  fieldLabel?: string;
  description?: string;
  isRequired?: boolean;
  cascadeDelete?: boolean;
  shareAccessLevel?: 'ReadOnly' | 'ReadWrite' | 'None';
  lookupFilter?: string;
  isActive?: boolean;
}

// ============================================
// DEFAULT MOCK DATA
// ============================================

const defaultRelationships: ObjectRelationship[] = [
  // Contact -> Client (Lookup)
  {
    id: 'rel_contact_client',
    sourceObjectId: 'obj_contact',
    sourceObjectName: 'Contact__c',
    sourceObjectLabel: 'Contact',
    fieldName: 'account__c',
    fieldLabel: 'Account',
    targetObjectId: 'obj_client',
    targetObjectName: 'Client__c',
    targetObjectLabel: 'Client',
    relationshipType: 'lookup',
    cascadeDelete: false,
    shareAccessLevel: 'None',
    childRelationshipName: 'Contacts',
    description: 'Links a contact to their parent account',
    isRequired: false,
    isStandard: true,
    isActive: true,
    createdBy: 'system',
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  },
  // Opportunity -> Client (Lookup)
  {
    id: 'rel_opportunity_client',
    sourceObjectId: 'obj_opportunity',
    sourceObjectName: 'Opportunity__c',
    sourceObjectLabel: 'Opportunity',
    fieldName: 'account__c',
    fieldLabel: 'Account',
    targetObjectId: 'obj_client',
    targetObjectName: 'Client__c',
    targetObjectLabel: 'Client',
    relationshipType: 'lookup',
    cascadeDelete: false,
    shareAccessLevel: 'None',
    childRelationshipName: 'Opportunities',
    description: 'Links an opportunity to the related account',
    isRequired: true,
    isStandard: true,
    isActive: true,
    createdBy: 'system',
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  },
  // Case -> Client (Master-Detail)
  {
    id: 'rel_case_client',
    sourceObjectId: 'obj_case',
    sourceObjectName: 'Case__c',
    sourceObjectLabel: 'Case',
    fieldName: 'account__c',
    fieldLabel: 'Account',
    targetObjectId: 'obj_client',
    targetObjectName: 'Client__c',
    targetObjectLabel: 'Client',
    relationshipType: 'master-detail',
    cascadeDelete: true,
    shareAccessLevel: 'ReadWrite',
    childRelationshipName: 'Cases',
    description: 'Links a case to the related account (master-detail)',
    isRequired: true,
    isStandard: true,
    isActive: true,
    createdBy: 'system',
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  },
  // Task -> Related To (Polymorphic Lookup)
  {
    id: 'rel_task_related',
    sourceObjectId: 'obj_task',
    sourceObjectName: 'Task__c',
    sourceObjectLabel: 'Task',
    fieldName: 'related_to__c',
    fieldLabel: 'Related To',
    targetObjectId: 'obj_client',
    targetObjectName: 'Client__c',
    targetObjectLabel: 'Client',
    relationshipType: 'lookup',
    cascadeDelete: false,
    shareAccessLevel: 'None',
    childRelationshipName: 'Tasks',
    description: 'Links a task to a related record',
    isRequired: false,
    isStandard: true,
    isActive: true,
    createdBy: 'system',
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  },
  // Lead -> Contact (converted to)
  {
    id: 'rel_lead_contact',
    sourceObjectId: 'obj_lead',
    sourceObjectName: 'Lead__c',
    sourceObjectLabel: 'Lead',
    fieldName: 'converted_contact__c',
    fieldLabel: 'Converted Contact',
    targetObjectId: 'obj_contact',
    targetObjectName: 'Contact__c',
    targetObjectLabel: 'Contact',
    relationshipType: 'lookup',
    cascadeDelete: false,
    shareAccessLevel: 'None',
    childRelationshipName: 'ConvertedLeads',
    description: 'Links to the contact created when this lead was converted',
    isRequired: false,
    isStandard: true,
    isActive: true,
    createdBy: 'system',
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  },
  // Opportunity -> Contact (Primary Contact)
  {
    id: 'rel_opportunity_contact',
    sourceObjectId: 'obj_opportunity',
    sourceObjectName: 'Opportunity__c',
    sourceObjectLabel: 'Opportunity',
    fieldName: 'primary_contact__c',
    fieldLabel: 'Primary Contact',
    targetObjectId: 'obj_contact',
    targetObjectName: 'Contact__c',
    targetObjectLabel: 'Contact',
    relationshipType: 'lookup',
    cascadeDelete: false,
    shareAccessLevel: 'None',
    childRelationshipName: 'OpportunitiesAsPrimary',
    description: 'The primary contact for this opportunity',
    isRequired: false,
    isStandard: true,
    isActive: true,
    createdBy: 'system',
    createdDate: '2024-01-01T00:00:00Z',
    lastModified: '2024-01-01T00:00:00Z'
  }
];

// ============================================
// PERSISTED STORAGE
// ============================================

export const persistedObjectRelationships = createPersistedMockData<ObjectRelationship>(
  'crm_object_relationships',
  defaultRelationships
);

// ============================================
// QUERY KEYS
// ============================================

export const relationshipKeys = {
  all: ['objectRelationships'] as const,
  byObject: (objectId: string) => [...relationshipKeys.all, 'object', objectId] as const,
  detail: (relationshipId: string) => [...relationshipKeys.all, 'detail', relationshipId] as const,
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get relationships where the object is the source (has the lookup field)
 */
function getRelationshipsFromObject(objectId: string): ObjectRelationship[] {
  const allRelationships = persistedObjectRelationships.get();
  return allRelationships.filter(r => r.sourceObjectId === objectId && r.isActive);
}

/**
 * Get relationships where the object is the target (is being referenced)
 */
function getRelationshipsToObject(objectId: string): ObjectRelationship[] {
  const allRelationships = persistedObjectRelationships.get();
  return allRelationships.filter(r => r.targetObjectId === objectId && r.isActive);
}

/**
 * Get all relationships involving an object (both directions)
 */
function getAllRelationshipsForObject(objectId: string): ObjectRelationship[] {
  const allRelationships = persistedObjectRelationships.get();
  return allRelationships.filter(
    r => (r.sourceObjectId === objectId || r.targetObjectId === objectId) && r.isActive
  );
}

// ============================================
// HOOKS
// ============================================

/**
 * Get all relationships in the system
 */
export function useAllRelationships() {
  return useQuery({
    queryKey: relationshipKeys.all,
    queryFn: async () => {
      if (USE_GOBODO_API) {
        const relationships = await getGobodoRelationshipTypes();
        return relationships.filter(r => r.isActive);
      } else {
        await new Promise(resolve => setTimeout(resolve, 300));
        return persistedObjectRelationships.get().filter(r => r.isActive);
      }
    },
    staleTime: 5 * 60 * 1000
  });
}

/**
 * Get relationships for a specific object (both directions)
 */
export function useObjectRelationships(objectId: string, options?: {
  direction?: 'from' | 'to' | 'both';
  includeInactive?: boolean;
}) {
  const queryClient = useQueryClient();
  const direction = options?.direction || 'both';
  const includeInactive = options?.includeInactive || false;

  return useQuery({
    queryKey: [...relationshipKeys.byObject(objectId), direction, includeInactive],
    queryFn: async () => {
      if (USE_GOBODO_API) {
        // Try to get the exact apiName from the objects list cache
        // Search through all cached object list queries (they may have different filter keys)
        const queryCache = queryClient.getQueryCache();
        const allObjectQueries = queryCache.findAll({ queryKey: objectKeys.all });
        
        let cachedObject: CustomObject | undefined;
        for (const query of allObjectQueries) {
          const data = query.state.data as CustomObject[] | undefined;
          if (data && Array.isArray(data)) {
            cachedObject = data.find(obj => obj.id === objectId);
            if (cachedObject) break;
          }
        }
        
        const exactKey = cachedObject?.apiName;
        
        // Resolve object key using cached apiName if available
        const objectKey = resolveObjectKey(objectId, exactKey);
        
        // Get object info for passing to relationship functions
        const objectName = cachedObject?.apiName || exactKey || objectKey;
        const objectLabel = cachedObject?.label || '';
        
        let relationships: ObjectRelationship[] = [];

        // Fetch relationships based on direction
        switch (direction) {
          case 'from':
            // Outgoing relationships (where this object is the source)
            relationships = await getGobodoObjectTypeOutgoingRelationships(
              objectKey,
              objectId,
              objectName,
              objectLabel
            );
            break;
          case 'to':
            // Incoming relationships (where this object is the target)
            relationships = await getGobodoObjectTypeIncomingRelationships(
              objectKey,
              objectId,
              objectName,
              objectLabel
            );
            break;
          default:
            // Both directions - fetch sequentially to avoid backend collisions
            const outgoing = await getGobodoObjectTypeOutgoingRelationships(
              objectKey, objectId, objectName, objectLabel
            );
            const incoming = await getGobodoObjectTypeIncomingRelationships(
              objectKey, objectId, objectName, objectLabel
            );
            relationships = [...outgoing, ...incoming];
        }

        // Apply inactive filter if needed
        if (!includeInactive) {
          relationships = relationships.filter(r => r.isActive);
        }

        return relationships;
      } else {
        await new Promise(resolve => setTimeout(resolve, 300));

        const allRelationships = persistedObjectRelationships.get();
        let filtered: ObjectRelationship[];

        switch (direction) {
          case 'from':
            filtered = allRelationships.filter(r => r.sourceObjectId === objectId);
            break;
          case 'to':
            filtered = allRelationships.filter(r => r.targetObjectId === objectId);
            break;
          default:
            filtered = allRelationships.filter(
              r => r.sourceObjectId === objectId || r.targetObjectId === objectId
            );
        }

        if (!includeInactive) {
          filtered = filtered.filter(r => r.isActive);
        }

        return filtered;
      }
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!objectId
  });
}

/**
 * Get a single relationship by ID
 */
export function useRelationship(relationshipId: string) {
  return useQuery({
    queryKey: relationshipKeys.detail(relationshipId),
    queryFn: async () => {
      if (USE_GOBODO_API) {
        // Extract key from relationshipId (remove 'rel_' prefix)
        // The relationship key is stored as the fieldName (lowercase)
        // We need to get all relationships and find the one matching the ID
        // since we don't have a direct way to get by ID
        const allRelationships = await getGobodoRelationshipTypes();
        return allRelationships.find(r => r.id === relationshipId) || null;
      } else {
        await new Promise(resolve => setTimeout(resolve, 200));
        const allRelationships = persistedObjectRelationships.get();
        return allRelationships.find(r => r.id === relationshipId) || null;
      }
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!relationshipId
  });
}

/**
 * Create a new relationship
 */
export function useCreateRelationship() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateRelationshipInput) => {
      if (USE_GOBODO_API) {
        // Look up source object's category from cache
        let sourceObjectCategory: 'core' | 'system' | 'custom' | undefined;
        try {
          const allObjects = queryClient.getQueryCache().findAll({ queryKey: objectKeys.all });
          const sourceObject = allObjects
            .flatMap(query => query.state.data as CustomObject[] || [])
            .find(obj => obj.id === input.sourceObjectId || obj.apiName === input.sourceObjectName);
          
          if (sourceObject?.category) {
            sourceObjectCategory = sourceObject.category;
          }
        } catch (error) {
          console.warn('Could not determine source object category, defaulting to custom:', error);
        }
        
        return createGobodoRelationshipType(input, sourceObjectCategory);
      } else {
        await new Promise(resolve => setTimeout(resolve, 500));

        const newRelationship: ObjectRelationship = {
          id: `rel_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          ...input,
          cascadeDelete: input.cascadeDelete ?? (input.relationshipType === 'master-detail'),
          shareAccessLevel: input.shareAccessLevel ?? (input.relationshipType === 'master-detail' ? 'ReadWrite' : 'None'),
          isRequired: input.isRequired ?? (input.relationshipType === 'master-detail'),
          isStandard: false,
          isActive: true,
          createdBy: 'current_user',
          createdDate: new Date().toISOString(),
          lastModified: new Date().toISOString()
        };

        persistedObjectRelationships.add(newRelationship);
        return newRelationship;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: relationshipKeys.all });
      queryClient.invalidateQueries({ queryKey: relationshipKeys.byObject(data.sourceObjectId) });
      queryClient.invalidateQueries({ queryKey: relationshipKeys.byObject(data.targetObjectId) });
    }
  });
}

/**
 * Update an existing relationship
 */
export function useUpdateRelationship() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: UpdateRelationshipInput) => {
      if (USE_GOBODO_API) {
        return updateGobodoRelationshipType(input);
      } else {
        await new Promise(resolve => setTimeout(resolve, 400));

        const allRelationships = persistedObjectRelationships.get();
        const index = allRelationships.findIndex(r => r.id === input.id);

        if (index === -1) {
          throw new Error('Relationship not found');
        }

        const updated: ObjectRelationship = {
          ...allRelationships[index],
          ...input,
          lastModified: new Date().toISOString()
        };

        allRelationships[index] = updated;
        persistedObjectRelationships.set(allRelationships);

        return updated;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: relationshipKeys.all });
      queryClient.invalidateQueries({ queryKey: relationshipKeys.byObject(data.sourceObjectId) });
      queryClient.invalidateQueries({ queryKey: relationshipKeys.byObject(data.targetObjectId) });
      queryClient.invalidateQueries({ queryKey: relationshipKeys.detail(data.id) });
    }
  });
}

export interface DeleteRelationshipPayload {
  relationshipId: string;
  relationship: ObjectRelationship;
}

/**
 * Delete a relationship (Gobodo: hard delete; mock: soft delete)
 */
export function useDeleteRelationship() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: string | DeleteRelationshipPayload) => {
      const relationshipId = typeof payload === 'string' ? payload : payload.relationshipId;
      const relationship = typeof payload === 'object' ? payload.relationship : undefined;

      if (USE_GOBODO_API) {
        if (relationship?.isStandard) {
          throw new Error('Cannot delete standard relationships');
        }
        await deleteGobodoRelationshipType(relationshipId);
        return relationship ?? { sourceObjectId: '', targetObjectId: '' };
      } else {
        await new Promise(resolve => setTimeout(resolve, 300));

        const allRelationships = persistedObjectRelationships.get();
        const rel = relationship ?? allRelationships.find(r => r.id === relationshipId);

        if (!rel) {
          throw new Error('Relationship not found');
        }

        if (rel.isStandard) {
          throw new Error('Cannot delete standard relationships');
        }

        const updated = allRelationships.map(r =>
          r.id === relationshipId
            ? { ...r, isActive: false, lastModified: new Date().toISOString() }
            : r
        );

        persistedObjectRelationships.set(updated);
        return rel;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: relationshipKeys.all });
      if (data?.sourceObjectId) {
        queryClient.invalidateQueries({ queryKey: relationshipKeys.byObject(data.sourceObjectId) });
      }
      if (data?.targetObjectId) {
        queryClient.invalidateQueries({ queryKey: relationshipKeys.byObject(data.targetObjectId) });
      }
    }
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Get human-readable relationship type label
 */
export function getRelationshipTypeLabel(type: RelationshipType): string {
  switch (type) {
    case 'lookup':
      return 'Lookup';
    case 'master-detail':
      return 'Master-Detail';
    default:
      return type;
  }
}

/**
 * Get relationship type color for badges
 */
export function getRelationshipTypeColor(type: RelationshipType): string {
  switch (type) {
    case 'lookup':
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300';
    case 'master-detail':
      return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300';
    default:
      return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
  }
}
